<template>
  <div>
    <section class="main-scn">
      <img :src="$getImage(`onesti-logo/onesti-logo-branco-${currentLocale}.svg`)" class="main-scn__logo" :alt="`${$t('alt.logo')} Onesti`">
      <h2>
        <span class="font-thin">Info & </span>
        <span class="font-condensed">Legal</span>
      </h2>
    </section>
    <scn class="relative overflow-hidden">
      <img :src="$getImage('onesti-logo/a-pattern.svg')" class="a-pattern" aria-hidden="true" alt="null">
      <div class="legal-advice-intro container">
        <h2 class="legal-advice-intro__title">
          {{ $t('legalAdvice.detail.title') }}
        </h2>
        <p class="legal-advice-intro__text">
          {{ $t('legalAdvice.detail.subtitle') }}
        </p>
      </div>
      <div class="divider" />
      <div class="scn__content container">
        <div class="column-text-ctn">
          <p v-for="paragraph in $t('legalAdvice.detail.paragraphs')" class="mb-2">
            <span class="hash-tag">
              {{ paragraph.hashTag }}
            </span>
            <span>
              {{ paragraph.text }}
            </span>
          </p>
        </div>
      </div>
    </scn>
    <scn color="grey">
      <section-header
        :title="$t('legalAdvice.clarifications.title')"
        subtitle=" "
        alignment="center"
      />
      <div class="scn__content container">
        <p class="mb-3">
          {{ $t('legalAdvice.clarifications.introduction') }}
        </p>
        <h3 class="mb-2">
          {{ $t('legalAdvice.clarifications.activityTitle') }}
        </h3>
        <p v-for="(paragraph, index) in $t('legalAdvice.clarifications.paragraphs')"
           class="mb-2"
           :class="{'font-bold text-pink' : index === 1}"
        >
          {{ paragraph }}
        </p>
        <div v-for="subsection in $t('legalAdvice.clarifications.subsections')" class="subsection mt-3">
          <h3 class="subsection__title">
            {{ subsection.title }}
          </h3>
          <p v-for="paragraph in subsection.paragraphs" class="mb-3">
            {{ paragraph }}
          </p>
        </div>
      </div>
    </scn>
    <scn>
      <section-header
        :title="$t('legalAdvice.privacy.title')"
        :subtitle="$t('legalAdvice.privacy.subtitle')"
        alignment="center"
      />
      <div class="scn__content container">
        <p>
          {{ $t('legalAdvice.privacy.introduction') }}
        </p>
        <ul class="list my-2">
          <li v-for="item in $t('legalAdvice.privacy.cookieUsageList')">
            {{ item }}
          </li>
        </ul>
        <h3 class="mb-2">
          {{ $t('legalAdvice.privacy.marketingTitle') }}
        </h3>
        <p v-for="item in $t('legalAdvice.privacy.paragraphs')" class="mb-3">
          {{ item }}
        </p>
        <ul class="list my-2">
          <li v-for="item in $t('legalAdvice.privacy.dataUsageList')" class="mb-2">
            <span class="font-bold">
              {{ item.title }}
            </span>
            <span>
              {{ item.text }}
            </span>
          </li>
        </ul>
        <p class="text-center my-3">
          {{ $t('legalAdvice.privacy.rightsSummary') }}
        </p>
        <h4>
          {{ $t('legalAdvice.privacy.rights.title') }}
        </h4>
        <ul class="list my-2">
          <li v-for="item in $t('legalAdvice.privacy.rights.rightsList')" class="mb-2">
            {{ item }}
          </li>
        </ul>
        <p v-for="paragraph in $t('legalAdvice.privacy.rights.infoRights')" class="mb-3">
          {{ paragraph }}
        </p>
        <ul class="list mb-3">
          <li class="mb-2">
            {{ $t('legalAdvice.privacy.rights.emailListItem') }}
          </li>
          <li>
            <span class="font-bold">
              {{ $t('legalAdvice.privacy.rights.requiredDataListItem.title') }}
            </span>
            <span>
              {{ $t('legalAdvice.privacy.rights.requiredDataListItem.text') }}
            </span>
          </li>
        </ul>
        <div v-for="item in $t('legalAdvice.privacy.rights.paragraphs')" class="mb-3">
          <h4>
            {{ item.title }}
          </h4>
          <div v-for="(paragraph, index) in item.text">
            <p v-if="index === 0">
              {{ paragraph }}
            </p>
            <a v-if="index === 1"
               href="https://tools.google.com/dlpage/gaoptout"
               class="text-pink font-bold"
               target="_blank"
            >
              {{ paragraph }}
            </a>
          </div>
        </div>
      </div>
    </scn>
    <scn color="black">
      <div class="legal-header-wrapper lg:ml-28 xl:ml-30 xxl:ml-0 xxl:mt-0">
        <section-header
          :title="$t('legalAdvice.privacy.reproduction.title')"
          :subtitle="$t('legalAdvice.privacy.reproduction.subtitle')"
          class="force-line-break"
        />
      </div>
      <div class="see-scn-img xxl:mt-3" />
      <div class="scn__content container">
        <p v-for="paragraph in $t('legalAdvice.privacy.reproduction.paragraphs')" class="mb-3">
          {{ paragraph }}
        </p>
      </div>
    </scn>
    <scn color="grey">
      <section-header
        :title="$t('legalAdvice.grateful.title')"
        :subtitle="$t('legalAdvice.grateful.subtitle')"
        alignment="center"
      />
      <div class="scn__content container">
        <p v-for="paragraph in $t('legalAdvice.grateful.paragraphs')" class="mb-3">
          {{ paragraph }}
        </p>
        <div class="logo-row logo-row--centered">
          <div class="logo">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.conception') }}
            </p>
            <img :src="$getImage(`onesti-logo/onesti-logo-${currentLocale}.svg`)" :alt="`${$t('alt.logo')} Onesti`">
          </div>
        </div>
        <div class="logo-row lg:my-3">
          <a class="logo logo--buu" href="https://buudigital.com.br/" target="_blank">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.layout') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-buu.png`)" :alt="`${$t('alt.logo')} Buu`">
          </a>
          <a class="logo" href="https://www.lelukas.com" target="_blank">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.programming') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-lelukas.svg`)" :alt="`${$t('alt.logo')} Lucas Almeida`">
          </a>
          <div class="logo">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.portugueseRevision') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-arrais.png`)" :alt="`${$t('alt.logo')} Arrais`">
          </div>
        </div>
        <div class="logo-row lg:my-3">
          <a class="logo" href="https://www.linkedin.com/in/yusefwateefthornton" target="_blank">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.english') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-yusef.png`)" :alt="`${$t('alt.logo')} Yusef`">
          </a>
          <a class="logo" href="http://www.guilhermeudo.com/index.html" target="_blank">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.spanish') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-guiudo.png`)" :alt="`${$t('alt.logo')} Guiudo`">
          </a>
          <div class="logo">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.chinese') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-lingchao.png`)" :alt="`${$t('alt.logo')} Lingchao`">
          </div>
        </div>
        <div class="logo-row logo-row--centered">
          <a class="logo" href="https://www.valledamidia.com.br/" target="_blank">
            <p class="logo__title">
              {{ $t('legalAdvice.grateful.accessory') }}
            </p>
            <img :src="$getImage(`partners-logos/logo-valle.png`)" :alt="`${$t('alt.logo')} Valle`">
          </a>
        </div>
      </div>
    </scn>
    <scn>
      <section-header
        :title="$t('legalAdvice.translation.title')"
        :subtitle="$t('legalAdvice.translation.subtitle')"
        alignment="center"
      />
      <div class="scn__content container">
        <p v-for="paragraph in $t('legalAdvice.translation.paragraphs')">
          {{ paragraph }}
        </p>
      </div>
    </scn>
  </div>
</template>

<script>
export default {
  name: 'LegalAdvice',
  computed: {
    currentLocale () {
      return this.$i18n.locale
    }
  }
}
</script>

<style scoped lang="sass">
@tailwind utilities

.main-scn
  @apply relative flex items-center justify-center text-white text-5xl w-full h-screen
  background-color: rgba(186, 52, 105, 0.6)
  background-size: cover
  background-image: url('~@/assets/images/vista-rua-com-predios@small.jpg')
  @screen md
    background-image: url('~@/assets/images/vista-rua-com-predios@medium.jpg')
  @screen xl
    background-image: url('~@/assets/images/vista-rua-com-predios@large.jpg')
  &__logo
    @apply absolute half-l -translate-x-50 w-40
    top: 100px
    @screen xxl
      top: 240px

.a-pattern
  @apply hidden absolute w-48
  right: -40px
  top: -50px
  @screen xl
    @apply block

.legal-advice-intro
  @apply text-center mb-5 mx-auto
  &__title
    @apply text-3xl text-grey-darker uppercase mb-2 mx-auto
    @screen md
      @apply text-5xl
    @screen lg
      width: 51rem
  &__text
    @apply leading-normal mx-auto
    max-width: 750px

.divider
  @apply h-px mb-1 mx-auto
  width: 80vw
  background: linear-gradient(90deg, transparent, #d8306a, transparent)
  @screen md
    width: 350px

.column-text-ctn
  columns: 1
  @screen lg
    columns: 2
  p
    @apply items-start
    page-break-inside: avoid

.hash-tag
  @apply text-pink font-bold

.subsection
  @apply mb-4
  &__title
    @apply text-2xl text-pink mb-2
    @screen md
      @apply text-4xl

.list
  list-style-type: none
  @apply pl-2
  li::before
    content: "\2022"
    @apply inline-block text-pink -ml-2 mr-1

.see-scn-img
  @apply h-48
  background-image: url('~@/assets/images/sections/pessoa-montando-blocos.jpg')
  background-size: cover
  background-repeat: no-repeat
  @screen lg
    @apply absolute pin-l h-48
    top: 40px
    width: 41vw
    &::after
      @apply no-content absolute pin-t bg-pink w-1 h-full
      right: -10px
  @screen xxl
    @apply static mx-auto
    &::after
      content: none

.legal-header-wrapper
  @screen lg
    width: 40vw
  @screen xxl
    width: auto

.force-line-break
  white-space: normal !important

.logo-row
  @apply flex flex-col
  @screen lg
    @apply flex-row justify-between
  &--centered
    @apply justify-center

.logo
  @apply text-center no-underline whitespace-no-wrap h-40 mb-3
  @screen lg
    @apply h-32 w-64
  &__title
    @apply text-black mb-2
  img
    @apply max-w-40
</style>
